/**
 * Query-key factory. Every TanStack Query key in the app is minted here so
 * the SSE handler (`api/sse.ts`) can invalidate by prefix without guessing
 * strings. Keys are tuples; the first element is the resource family.
 *
 * Filters are passed as plain objects and compared structurally by the
 * query client, so callers must strip `undefined` for stable keys — `qs()`
 * in `client.ts` already ignores them on the wire.
 */

/** Any plain filter object (route search, list params). Values must be JSON-comparable. */
type Filters = object

function clean<T extends Filters>(f: T | undefined): Partial<T> {
  if (!f) return {}
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') out[k] = v
  return out as Partial<T>
}

export const keys = {
  health: () => ['health'] as const,
  settings: () => ['settings'] as const,
  agyModels: () => ['ai', 'agy', 'models'] as const,
  geminiModels: () => ['ai', 'gemini', 'models'] as const,
  openrouterModels: () => ['ai', 'openrouter', 'models'] as const,

  index: {
    all: () => ['index'] as const,
    status: () => ['index', 'status'] as const,
    runs: () => ['index', 'runs'] as const,
    run: (id: number) => ['index', 'runs', id] as const,
    exclusions: () => ['index', 'exclusions'] as const,
  },

  assets: {
    all: () => ['assets'] as const,
    list: (filters?: Filters) => ['assets', 'list', clean(filters)] as const,
    detail: (uid: string) => ['assets', 'detail', uid] as const,
    search: (q: string, filters?: Filters) => ['assets', 'search', q, clean(filters)] as const,
  },
  categories: (kind?: string) => ['categories', kind ?? 'all'] as const,

  vocab: {
    all: () => ['vocab'] as const,
    list: (domain?: string, includeDisabled = false) => ['vocab', 'list', domain ?? 'all', includeDisabled] as const,
    proposed: () => ['vocab', 'proposed'] as const,
  },

  annotation: {
    all: () => ['annotation'] as const,
    doc: (uid: string, rev?: number) => ['annotation', uid, rev ?? 'head'] as const,
    revisions: (uid: string) => ['annotation', uid, 'revisions'] as const,
  },

  proposals: {
    all: () => ['proposals'] as const,
    list: (filters?: Filters) => ['proposals', 'list', clean(filters)] as const,
    detail: (id: number) => ['proposals', 'detail', id] as const,
    next: (filters?: Filters) => ['proposals', 'next', clean(filters)] as const,
  },

  jobs: {
    all: () => ['jobs'] as const,
    list: (filters?: Filters) => ['jobs', 'list', clean(filters)] as const,
    detail: (id: number) => ['jobs', 'detail', id] as const,
    stats: () => ['jobs', 'stats'] as const,
  },

  stories: {
    all: () => ['stories'] as const,
    list: (filters?: Filters) => ['stories', 'list', clean(filters)] as const,
    detail: (uid: string) => ['stories', 'detail', uid] as const,
    bundle: (uid: string) => ['stories', 'bundle', uid] as const,
    exportEstimate: (uid: string) => ['stories', 'export-estimate', uid] as const,
    presets: (minTier?: string) => ['stories', 'presets', minTier ?? 'auto'] as const,
    catalog: (world: string, minTier?: string) => ['stories', 'catalog', world, minTier ?? 'auto'] as const,
  },

  tts: {
    voices: () => ['tts', 'voices'] as const,
    sample: (role: string, voice?: string) => ['tts', 'sample', role, voice ?? ''] as const,
  },

  costs: {
    summary: (filters?: Filters) => ['costs', 'summary', clean(filters)] as const,
    ledger: (filters?: Filters) => ['costs', 'ledger', clean(filters)] as const,
  },

  media: {
    cacheStats: () => ['media', 'cache', 'stats'] as const,
  },
} as const
